import React, { useState } from "react";
import { makeStyles, useTheme, Dropdown } from "@seahorse/ui";

interface CoHedgerLandingPageProps {
  onSubmit: (params: {
    currencyPair: string;
    assetType: string;
    executionType: string;
    expirationDate: string;
  }) => void;
}

const CURRENCY_LIST = [
  { label: "USDJPY", value: "USDJPY" },
  { label: "EURUSD", value: "EURUSD" },
  { label: "GBPUSD", value: "GBPUSD" },
  { label: "USDCHF", value: "USDCHF" },
  { label: "USDCAD", value: "USDCAD" },
  { label: "AUDUSD", value: "AUDUSD" },
  { label: "NZDUSD", value: "NZDUSD" },
  { label: "USDNOK", value: "USDNOK" },
  { label: "USDSEK", value: "USDSEK" },
  { label: "USDINR", value: "USDINR" },
].sort((a, b) => a.label.localeCompare(b.label));

const ASSET_TYPES = [
  { label: "STOCK", value: "STOCK" },
  { label: "BOND", value: "BOND" },
];

const EXECUTION_TYPES = [
  { label: "SELL", value: "SELL" },
  { label: "BUY", value: "BUY" },
];

const CoHedgerLandingPage: React.FC<CoHedgerLandingPageProps> = ({
  onSubmit,
}) => {
  const theme = useTheme();

  const [currencyPair, setCurrencyPair] = useState("");
  const [assetType, setAssetType] = useState("");
  const [executionType, setExecutionType] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const next: Record<string, string> = {};
    if (!currencyPair) next.currencyPair = "Currency pair is required";
    if (!assetType) next.assetType = "Asset type is required";
    if (!executionType) next.executionType = "Execution type is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSubmit({
      currencyPair,
      assetType,
      executionType,
      expirationDate: expirationDate.trim() || "None",
    });
  };

  const useStyles = makeStyles({
    wrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      width: "100%",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
      padding: 24,
    },
    card: {
      width: "100%",
      maxWidth: 480,
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.04)",
      padding: "32px 28px",
      display: "flex",
      flexDirection: "column",
      gap: 20,
    },
    header: {
      textAlign: "center" as const,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    title: {
      color: "#E0E0E0",
      fontSize: 20,
      fontWeight: 700,
      margin: 0,
    },
    subtitle: {
      color: "#E0E0E0",
      fontSize: 13,
      opacity: 0.6,
      margin: 0,
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    label: {
      color: "#E0E0E0",
      fontSize: 13,
      fontWeight: 600,
      opacity: 0.9,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      fontSize: 14,
      outline: "none",
      "&::placeholder": {
        color: "rgba(224,224,224,0.35)",
      },
    },
    error: {
      color: "#ff6b6b",
      fontSize: 12,
      margin: 0,
    },
    button: {
      marginTop: 8,
      cursor: "pointer",
      padding: "12px 0",
      borderRadius: 10,
      border: "none",
      background: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
      fontSize: 15,
      fontWeight: 700,
      transition: "opacity 0.15s ease",
      "&:hover": {
        opacity: 0.9,
      },
    },
    optionalHint: {
      color: "#E0E0E0",
      fontSize: 11,
      opacity: 0.45,
      fontWeight: 400,
    },
  });

  const classes = useStyles();

  return (
    <div className={classes.wrapper}>
      <div className={classes.card}>
        <div className={classes.header}>
          <p className={classes.title}>EMAlpha Hedge Assistant</p>
          <p className={classes.subtitle}>
            Enter your hedging parameters to begin the analysis
          </p>
        </div>

        {/* Currency Pair */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>Currency Pair</span>
          <Dropdown
            items={CURRENCY_LIST}
            value={currencyPair}
            onChange={(value: any) => {
              setCurrencyPair(value);
              setErrors((prev) => ({ ...prev, currencyPair: "" }));
            }}
            placeholder="Select currency pair"
            fullWidth
          />
          {errors.currencyPair && (
            <p className={classes.error}>{errors.currencyPair}</p>
          )}
        </div>

        {/* Asset Type */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>Asset Type</span>
          <Dropdown
            items={ASSET_TYPES}
            value={assetType}
            onChange={(value: any) => {
              setAssetType(value);
              setErrors((prev) => ({ ...prev, assetType: "" }));
            }}
            placeholder="Select asset type"
            fullWidth
          />
          {errors.assetType && (
            <p className={classes.error}>{errors.assetType}</p>
          )}
        </div>

        {/* Execution Type */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>Execution Type</span>
          <Dropdown
            items={EXECUTION_TYPES}
            value={executionType}
            onChange={(value: any) => {
              setExecutionType(value);
              setErrors((prev) => ({ ...prev, executionType: "" }));
            }}
            placeholder="Select execution type"
            fullWidth
          />
          {errors.executionType && (
            <p className={classes.error}>{errors.executionType}</p>
          )}
        </div>

        {/* Expiration Date */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>
            Expiration Date{" "}
            <span className={classes.optionalHint}>(optional)</span>
          </span>
          <input
            className={classes.input}
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            placeholder="e.g. 2026-02-19 18:05:00"
          />
        </div>

        {/* Submit */}
        <button
          type="button"
          className={classes.button}
          onClick={handleSubmit}
        >
          Analyze Hedge
        </button>
      </div>
    </div>
  );
};

export default CoHedgerLandingPage;
